import { m } from 'framer-motion';
import Link from 'next/link';
import type { CSSProperties, ReactNode, RefObject, UIEvent } from 'react';
import { useState } from 'react';
import type { IHighlightingButton } from './HighlightableButton.interface';
import { StyleElem, classNameForElement, styleForElement } from './HighlightableButton.style';

export interface IHighlightingButtonPrv {
    children?: ReactNode;
    style?: CSSProperties;
    isHighlightable?: boolean;
    onClick?: (event: UIEvent) => void;
    onHighlight?: () => void;
    onRestore?: () => void;
}

const withNextLink = <P extends Omit<IHighlightingButton, 'isLink'>>(
    renderer: (props: P) => JSX.Element,
    props: P,
) =>
    props.href ? (
        <Link href={props.href} passHref scroll={false} prefetch={false}>
            {renderer(props)}
        </Link>
    ) : (
        renderer(props)
    );

function useHighlightRestore(opacityRestore: string | number = 1): {
    setHighlighted: () => void;
    setDefault: () => void;
    isHighlighted: boolean;
    opacity: string | number;
} {
    const [isHighlighted, setIsHighlighted] = useState<boolean>(false);

    /* Try and get dim by 30% value from #opacityIn otherwise fall back to 70% */
    const opacityDim = !Number.isNaN(Number(opacityRestore))
        ? Math.max(Number(opacityRestore) - 0.3, 0.0)
        : 0.7;

    return {
        setHighlighted: () => {
            setIsHighlighted(true);
        },
        setDefault: () => {
            setIsHighlighted(false);
        },
        isHighlighted,
        opacity: isHighlighted ? opacityDim : opacityRestore,
    };
}

function HighlightableButton(props: Omit<IHighlightingButton, 'isLink'>) {
    const style = (e: StyleElem, overrides?: CSSProperties) => styleForElement(e, props, overrides);
    const className = (element: StyleElem) => classNameForElement(element, props);
    const { setHighlighted, setDefault, opacity } = useHighlightRestore(props.style?.opacity ?? 1);

    const highlightCallbacks = props.highlightable && {
        onTouchStart: setHighlighted,
        onTouchEnd: setDefault,
        onTouchCancel: setDefault,
        onMouseDown: setHighlighted,
        onMouseUp: setDefault,
        onMouseLeave: setDefault,
    };

    return (
        <m.button
            aria-hidden={props.hideOnAccessibilityTree}
            aria-label={props.description}
            ref={props.ref as RefObject<HTMLButtonElement> | undefined}
            animate={props.animate}
            variants={props.variants}
            className={`${props.className ?? ''} ${className(StyleElem.Container)}`}
            style={{ ...style(StyleElem.Container, { opacity }), ...props.style }}
            {...highlightCallbacks}
            onClick={props.onClick}
        >
            {props.children}
        </m.button>
    );
}

function HighlightableLink(props: Omit<IHighlightingButton, 'isLink'>) {
    const style = (e: StyleElem, overrides?: CSSProperties) => styleForElement(e, props, overrides);
    const className = (element: StyleElem) => classNameForElement(element, props);
    const { setHighlighted, setDefault, opacity } = useHighlightRestore(props.style?.opacity);

    const highlightCallbacks = props.highlightable && {
        onTouchStart: setHighlighted,
        onTouchEnd: setDefault,
        onTouchCancel: setDefault,
        onMouseDown: setHighlighted,
        onMouseUp: setDefault,
        onMouseLeave: setDefault,
    };

    return (
        <m.a
            aria-hidden={props.hideOnAccessibilityTree}
            aria-label={props.description}
            href={props.href}
            ref={props.ref as RefObject<HTMLAnchorElement> | undefined}
            animate={props.animate}
            variants={props.variants}
            className={`${props.className ?? ''} ${className(StyleElem.Container)}`}
            style={{ ...style(StyleElem.Container, { opacity }), ...props.style }}
            {...highlightCallbacks}
            onClick={props.onClick}
        >
            {props.children}
        </m.a>
    );
}

function HighlightableButtonLink(props: IHighlightingButton): JSX.Element {
    return props.isLink ? withNextLink(HighlightableLink, props) : HighlightableButton(props);
}

export default HighlightableButtonLink;
